import { describeEdit, describeEditOutcome } from "./assignmentEdit";

/**
 * W11b — THE THREE THINGS A COACH CAN DO TO ONE ASSIGNED DAY, IN ONE PLACE.
 *
 * 🔴 WHY IT IS A MODULE AND NOT THREE MORE HANDLERS. The Training tab's list already performs
 * hide/unhide, reschedule and remove — written inline in the component. The calendar's day-card
 * `⋯` menu (§8g) performs the SAME three. Two surfaces writing the same rows through two copies
 * of the same three statements is this repo's defining bug class waiting to happen: the first
 * change that touches one of them leaves the other behind, and nothing turns red. **Both surfaces
 * call these.**
 *
 * ⚠️ AND THE MENU CARRIES NOTHING THE LIST CANNOT ALREADY DO. No Log — the coach does not log for
 * the athlete. No Copy — there is no paste anywhere in this app (`EVERFIT-MAP` §27.5), and a copy
 * with no paste is a menu item that teaches a coach the product is broken.
 *
 * ⚠️ THE CLIENT IS AN ARGUMENT, NOT AN IMPORT, so these are testable without a network and
 * without a mocking framework — the same shape `endCoaching` already uses.
 */

/** The narrow slice of the Supabase client these need. */
export type AssignmentWriter = {
  from: (table: string) => {
    update: (values: Record<string, unknown>) => {
      eq: (column: string, value: string) => Promise<{ error: { message: string } | null }>;
    };
    delete: () => {
      eq: (column: string, value: string) => Promise<{ error: { message: string } | null }>;
    };
  };
};

/** What a caller shows afterwards. One sentence, already shaped by `describeEdit`. */
export type ActionResult = { ok: boolean; said: string };

const TABLE = "coach_assignments";

/**
 * Move an assignment to another day.
 *
 * ⚠️ THE CALLER CHECKS FIRST. `checkReschedule` decides whether a day is free; this performs the
 * write and reports what the SERVER said. The duplicate-day index still refuses independently —
 * **23505, `coach_assignments_one_source_per_day_idx`** — and `describeEditOutcome` turns that
 * into words. A client-side check that agreed with itself and skipped the server's refusal is
 * exactly the guard-with-no-teeth this repo keeps paying for.
 */
export async function rescheduleAssignment(
  db: AssignmentWriter, id: string, toISO: string, humanDate: string,
): Promise<ActionResult> {
  const { error } = await db.from(TABLE).update({ scheduled_date: toISO }).eq("id", id);
  return {
    ok: error === null,
    said: describeEdit(describeEditOutcome(error), {
      done: `Moved to ${humanDate}.`, verb: "move it",
    }),
  };
}

/**
 * 🔴 W137 (R7) — an assignment's order on its day (`day_position`, 0 first), written by the day drag. The COACH's write:
 * the client update guard refuses this column to anyone else (`20261002120000`, rehearsed per identity). Its `said` is
 * read only on a refusal — a reorder that lands says nothing; the card is simply where it was dropped.
 */
export async function setDayPosition(
  db: AssignmentWriter, id: string, position: number,
): Promise<ActionResult> {
  const { error } = await db.from(TABLE).update({ day_position: position }).eq("id", id);
  return {
    ok: error === null,
    said: describeEdit(describeEditOutcome(error), { done: "Moved.", verb: "move it" }),
  };
}

/**
 * Hide an assignment from the client, or show it again.
 *
 * ⚠️ HIDDEN IS ENFORCED BY THE CLIENT'S OWN READ POLICY (`client_id = auth.uid() AND
 * hidden_from_client = false`), not by this screen — so a hidden row is genuinely unreadable by
 * them rather than merely undrawn. Said here because a coach's confidence in it is the whole
 * value of the action.
 */
export async function setAssignmentHidden(
  db: AssignmentWriter, id: string, hidden: boolean,
): Promise<ActionResult> {
  const { error } = await db.from(TABLE).update({ hidden_from_client: hidden }).eq("id", id);
  return {
    ok: error === null,
    said: describeEdit(describeEditOutcome(error), {
      done: hidden ? "Hidden from them." : "Visible to them again.",
      verb: hidden ? "hide it" : "show it",
    }),
  };
}

/**
 * Remove an assignment.
 *
 * ⚠️ THE CALLER CONFIRMS, AND `removalConsequence` IS WHAT IT CONFIRMS WITH — a removal can
 * destroy logged sets, and a confirm that understates that is worse than none. This performs the
 * delete only.
 */
export async function removeAssignment(
  db: AssignmentWriter, id: string,
): Promise<ActionResult> {
  const { error } = await db.from(TABLE).delete().eq("id", id);
  return {
    ok: error === null,
    said: describeEdit(describeEditOutcome(error), {
      done: "Removed.", verb: "remove it",
    }),
  };
}

/**
 * The menu's items for one assignment — decided here so the two surfaces cannot offer different
 * ones.
 *
 * ⚠️ AN ITEM IS ABSENT, NEVER DISABLED-WITHOUT-A-REASON. A greyed row with no explanation is a
 * coach wondering what they did wrong; where an action genuinely cannot apply, the item is not
 * drawn and the card says why elsewhere.
 */
export type MenuItem =
  | { kind: "view"; label: string }
  /* QUEUE N2b-2 — EVERFIT-MAP §407: `Print workout` is theirs on every day, trained or not; it prints this ONE workout as
     their grid (`WorkoutPrintSheet`) and changes nothing, so it stays on a record too. */
  | { kind: "print"; label: string }
  | { kind: "hide"; label: string; hidden: boolean }
  | { kind: "reschedule"; label: string }
  | { kind: "note"; label: string }
  | { kind: "remove"; label: string }
  /* W137 R6b — the card's at-rest COPY icon: this day's workout onto another day of this client (the day window in its
     copy mode, `Copy to…`). Not one of `menuItems`' words — an icon beside the title; in the ⋯ only on a touch screen (`cardMenuItems`). */
  | { kind: "copy"; label: string }
  /* William's item 4 (2026-10-15) — the card's at-rest PENCIL: the workout BUILDER on this day's copy (add, remove, reorder,
     rename — what the per-exercise editor cannot). Drawn as an icon beside Copy (a word in the ⋯ only on touch), and only on a day
     not yet trained (`canEditWorkout`): a trained day is a record. */
  | { kind: "edit"; label: string };

/** W137 R6b — the two at-rest icons' actions: Copy opens the day window in copy mode; a plain click on the 4-arrow handle
 *  (no drag) opens the SAME window `Move…` opens, so the icon is never a dead click. */
export const COPY_ITEM: MenuItem = { kind: "copy", label: "Copy to…" };
export const EDIT_ITEM: MenuItem = { kind: "edit", label: "Edit workout" };
/** William's item 4 — whether a card offers the pencil: a WORKOUT (never a task) on a day not yet trained. */
export function canEditWorkout(a: { isTask: boolean; trained?: boolean }): boolean {
  return !a.isTask && a.trained !== true;
}
export const MOVE_ITEM: MenuItem = { kind: "reschedule", label: "Move…" };
/** QUEUE N2b-2 — their word, `Print workout` (EVERFIT-MAP §407). No ellipsis: the browser's own dialog follows, not ours. */
export const PRINT_ITEM: MenuItem = { kind: "print", label: "Print workout" };

/** 10-16 — THE CALENDAR CARD'S `⋯`, on BOTH pointer kinds since the hover row's brief (10-16, later): each action is drawn
 *  ONCE per card per pointer kind.
 *  - `glyphs: true` (a screen with hover — the pencil, Copy and 4-arrow drawn in the hovered card's row): only what no glyph
 *    does — `hoverItems`, so no `Edit workout`, no `Copy to…`, no `Move…`. These were the foot words, which covered the card's
 *    date and name (MEASURED 10-16); they are the menu now.
 *  - `glyphs: false` (no hover — the three are not drawn, at 1024 they left the name 0 px): the menu is their door too —
 *    `Edit workout` (a day not yet trained) and `Copy to…` after View, and `Move…` its word as before. */
export function cardMenuItems(a: { hiddenFromClient: boolean; trained?: boolean; canEdit: boolean; glyphs: boolean }): MenuItem[] {
  if (a.glyphs) return hoverItems(a);
  const all = menuItems(a);
  const at = all.findIndex((m) => m.kind === "view") + 1;
  return [...all.slice(0, at), ...(a.canEdit ? [EDIT_ITEM] : []), COPY_ITEM, ...all.slice(at)];
}

/** What the `⋯` holds where the glyphs are drawn: every word but `Move…` (the 4-arrow's click), with Edit and Copy never in
 *  `menuItems` to begin with — one door per action per card. */
export function hoverItems(a: { hiddenFromClient: boolean; trained?: boolean }): MenuItem[] {
  return menuItems(a).filter((m) => m.kind !== "reschedule");
}

/* 🔴 W22(a) — THE MENU IS COMPUTED FROM THE WORKOUT'S STATE, NOT A FIXED LIST WITH GREYED ROWS.
 * `EVERFIT-MAP` §2b-ter depth 3, read live: on a workout that is NOT completed theirs offers six
 * items including `Hide from client` and `Delete`; on a COMPLETED one it offers four, and those
 * two are **gone** — replaced by `Save logged workout to library`, which only exists after.
 *
 * Applied to OUR three, that empties the menu on a trained day, and that is the honest answer
 * rather than a shortfall: every one of ours changes what the athlete was asked to do, and a day
 * they have already trained is a RECORD. Hiding it hides work they performed; rescheduling it
 * moves a day that happened; removing it destroys logged sets.
 *
 * ⚠️ THEIR REMAINING ITEMS ARE NOT INVENTED HERE. (`Print workout` IS built since QUEUE N2b-2.) `Copy` has no paste
 * anywhere in this app (§27.5), and `Save workout to library` / `Save logged workout to library`
 * is §23g — filed, not stubbed. So a trained day draws NO `⋯` at all, and `AssignmentMenu` draws
 * nothing rather than an empty popover.
 *
 * ⚠️ AND THE TRAINING TAB'S LIST STILL OFFERS REMOVE ON A TRAINED DAY — DELIBERATELY, SAID HERE
 * SO IT IS NOT READ AS DRIFT. That surface is where destructive work is done with its cost in
 * front of the coach: `removalConsequence` prints the number of logged sets the removal would
 * destroy, counted across every session on that day. A glance-level popover cannot carry that
 * sentence, so the glance does not offer the action.
 */
/* 🔴 W63 (2026-09-24) — THE MENU IS THE WAY INTO A DAY NOW, SO IT IS NEVER EMPTY. The radio wall
 * under the calendar (one pill per assignment, 60+ on his client) and the "This day" panel it drove
 * are gone; every item but Hide opens ONE day window holding the controls and the planned-vs-
 * performed review. Fable's bound, the same rule as above: a TRAINED day is a record, so its menu
 * is read-only — `View` and `Note…` — and Move / Hide / Remove exist only before completion.
 * `Remove…` carries an ellipsis because it asks: the window prints what a removal would destroy
 * and arms before it acts. */
export function menuItems(a: { hiddenFromClient: boolean; trained?: boolean }): MenuItem[] {
  /* 🔴 REMOVE… STAYS ON A TRAINED DAY (2026-09-24, his live report: *"i see no way of
     unassigning a workout"*). The list under the calendar used to be where a trained day was
     removed with its cost printed; W63 deleted that list and the menu had inherited the old
     "glance-level popover cannot carry the sentence" rule — so for one day nothing in the product
     could remove a trained assignment. The menu is the way into the day window NOW, and the
     window prints `removalConsequence` (logged sets stay, the comparison goes) and arms before it
     acts, which is exactly the surface the old rule wanted. Move and Hide stay off a record. */
  if (a.trained === true) {
    return [
      { kind: "view", label: "View" },
      PRINT_ITEM,
      { kind: "note", label: "Note…" },
      { kind: "remove", label: "Remove…" },
    ];
  }
  return [
    { kind: "view", label: "View" },
    PRINT_ITEM,
    { kind: "hide", label: a.hiddenFromClient ? "Unhide" : "Hide from client", hidden: !a.hiddenFromClient },
    { kind: "reschedule", label: "Move…" },
    { kind: "note", label: "Note…" },
    { kind: "remove", label: "Remove…" },
  ];
}
